import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { FaCalendarAlt, FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import "./css/BeeHiveModal.css";

const HONEYCOMB_CAPACITY = 15;
const CHUNK_SIZE = 5;

const formatDate = (input) => {
  if (!input) {
    return "TBD";
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return "TBD";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const DEFAULT_EVENT_IMAGE = "/default-event-image.png";

const getImageUrl = (event) => {
  if (!event || typeof event !== "object") {
    return null;
  }

  return event.file_url || event.image_url || event.coverimage || DEFAULT_EVENT_IMAGE;
};

const getSeatAvailability = (event) => {
  const capacityRaw = event?.capacity ?? event?.total_tickets_planned ?? 0;
  const soldRaw = event?.sold_tickets ?? event?.soldTickets ?? 0;

  const capacity = Number.isFinite(Number(capacityRaw)) ? Number(capacityRaw) : 0;
  const sold = Number.isFinite(Number(soldRaw)) ? Number(soldRaw) : 0;
  const available = capacity > 0 ? Math.max(0, capacity - sold) : 0;

  return { capacity, sold, available };
};

const BeeHiveModal = ({ events, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const contentRef = useRef(null);
  const gridRef = useRef(null);

  const preparedEvents = useMemo(() => {
    if (!Array.isArray(events)) {
      return [];
    }

    return events.slice(0, HONEYCOMB_CAPACITY);
  }, [events]);

  const cells = useMemo(() => {
    if (preparedEvents.length >= HONEYCOMB_CAPACITY) {
      return preparedEvents;
    }

    const placeholders = Array.from({ length: HONEYCOMB_CAPACITY - preparedEvents.length }, (_, index) => ({
      placeholder: true,
      id: `placeholder-${index}`,
    }));

    return [...preparedEvents, ...placeholders];
  }, [preparedEvents]);

  const rows = useMemo(() => {
    const chunked = [];

    for (let i = 0; i < cells.length; i += CHUNK_SIZE) {
      const row = cells.slice(i, i + CHUNK_SIZE);

      if (row.length < CHUNK_SIZE) {
        const fillers = Array.from({ length: CHUNK_SIZE - row.length }, (_, index) => ({
          placeholder: true,
          id: `filler-${i}-${index}`,
        }));

        chunked.push([...row, ...fillers]);
      } else {
        chunked.push(row);
      }
    }

    return chunked;
  }, [cells]);

  const handleOverlayClick = (event) => {
    if (event.target.classList.contains("beehive-overlay")) {
      onClose?.();
    }
  };

  const handleCellClick = (cell) => {
    if (cell.placeholder) {
      return;
    }

    const eventId = cell.event_id ?? cell.id;
    if (!eventId) {
      return;
    }

    onClose?.();
    navigate(`/customerviewevent/${eventId}`);
  };

  useEffect(() => {
    if (window.innerWidth > 520) {
      return;
    }

    const contentEl = contentRef.current;
    const gridEl = gridRef.current;

    if (contentEl && gridEl) {
      const scrollLeft = Math.max(0, (gridEl.offsetWidth - contentEl.clientWidth) / 2);
      contentEl.scrollTo({ left: scrollLeft, behavior: "instant" });
    }
  }, [rows]);

  return (
    <div className="beehive-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <div className="beehive-modal">
        <button className="beehive-close" onClick={onClose} aria-label="Close Event Hive">
          <FaTimes />
        </button>

        <div className="beehive-content" ref={contentRef}>
          <div className="beehive-grid" ref={gridRef}>
            {rows.map((row, rowIndex) => (
              <div
                key={`beehive-row-${rowIndex}`}
                className={`beehive-row ${rowIndex % 2 === 1 ? "offset" : ""}`}
              >
                {(() => {
                  const rowLength = row.length;
                  const rowBaseIndex = rowIndex * rowLength;

                  return (
                    <>
                      {row.map((cell, cellIndex) => {
                        const globalIndex = rowBaseIndex + cellIndex;

                        return (
                          <div
                            key={cell.event_id ?? cell.id ?? `${rowIndex}-${cellIndex}`}
                            className={`honeycomb-cell ${cell.placeholder ? "placeholder" : "filled"}`}
                            style={{ animationDelay: `${globalIndex * 70}ms` }}
                            onClick={() => handleCellClick(cell)}
                            role={cell.placeholder ? undefined : "button"}
                            tabIndex={cell.placeholder ? undefined : 0}
                            onKeyDown={(event) => {
                              if (!cell.placeholder && (event.key === "Enter" || event.key === " ")) {
                                event.preventDefault();
                                handleCellClick(cell);
                              }
                            }}
                          >
                            <div className="honeycomb-core">
                              {cell.placeholder ? (
                                <div className="honeycomb-placeholder">
                                  <span className="placeholder-title">Event coming soon</span>
                                </div>
                              ) : (
                                <div className="honeycomb-core-content">
                                  <span className="core-name">{cell.name ?? "Unnamed Event"}</span>
                                  <span className="core-meta">{cell.location || "Location soon"}</span>
                                  {(() => {
                                    const { capacity, available } = getSeatAvailability(cell);
                                    if (!capacity) {
                                      return <span className="core-meta">Capacity TBD</span>;
                                    }

                                    return (
                                      <span className="core-meta">
                                        {available > 0
                                          ? `${available.toLocaleString()} seats left`
                                          : "Sold out"}
                                      </span>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>

                            {!cell.placeholder && (
                              <div className="honeycomb-card">
                                <div className="honeycomb-card-body">
                                  <span className="honeycomb-name">{cell.name ?? "Unnamed Event"}</span>
                                  <div className="honeycomb-meta">
                                    <FaCalendarAlt />
                                    <span>{formatDate(cell.date)}</span>
                                  </div>
                                  <div className="honeycomb-meta">
                                    <FaMapMarkerAlt />
                                    <span>{cell.location || "Location soon"}</span>
                                  </div>
                                  <div className="honeycomb-meta capacity">
                                    {(() => {
                                      const { capacity, available } = getSeatAvailability(cell);
                                      if (!capacity) {
                                        return <span>Capacity: TBD</span>;
                                      }

                                      return (
                                        <>
                                          <span className="capacity-label">
                                            {available > 0 ? "Seats left" : "Sold out"}
                                          </span>
                                          <span>
                                            {available > 0
                                              ? `${available.toLocaleString()} of ${capacity.toLocaleString()}`
                                              : `${capacity.toLocaleString()} total`}
                                          </span>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

BeeHiveModal.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func,
};

BeeHiveModal.defaultProps = {
  events: [],
  onClose: undefined,
};

export default BeeHiveModal;
